import { useCallback, useEffect, useState } from "react";
import { api, ApiError } from "../lib/api";
import type { Card, CardTransferRule, Person, RewardCurrency, TransferPartner, TransferRoute } from "../lib/types";
import { formatINR, formatPoints } from "../lib/format";
import { Badge, EmptyState, ErrorBanner, Modal, Panel, Spinner } from "./ui";
import { TransferCapSchemes } from "./TransferCapSchemes";

const CAP_PERIODS = [
  { key: "MONTH", label: "per month" },
  { key: "QUARTER", label: "per quarter" },
  { key: "YEAR", label: "per calendar year" },
  { key: "ANNIVERSARY_YEAR", label: "per card year" },
  { key: "LIFETIME", label: "lifetime" },
];

const periodLabel = (key: string) => CAP_PERIODS.find((p) => p.key === key)?.label ?? key;

export function TransferSetup({
  onError,
  onNotice,
}: {
  onError: (m: string) => void;
  onNotice: (m: string) => void;
}) {
  const [partners, setPartners] = useState<TransferPartner[] | null>(null);
  const [routes, setRoutes] = useState<TransferRoute[]>([]);
  const [rules, setRules] = useState<CardTransferRule[]>([]);
  const [currencies, setCurrencies] = useState<RewardCurrency[]>([]);
  const [cards, setCards] = useState<Card[]>([]);

  const [partnerModal, setPartnerModal] = useState<{ open: boolean; partner: TransferPartner | null }>({ open: false, partner: null });
  const [routeModal, setRouteModal] = useState<{ open: boolean; route: TransferRoute | null }>({ open: false, route: null });
  const [cardModal, setCardModal] = useState<Card | null>(null);

  const load = useCallback(async () => {
    try {
      const [p, r, cr, cu, c] = await Promise.all([
        api.get<{ partners: TransferPartner[] }>("/transfers/partners"),
        api.get<{ routes: TransferRoute[] }>("/transfers/routes"),
        api.get<{ rules: CardTransferRule[] }>("/transfers/card-rules"),
        api.get<{ currencies: RewardCurrency[] }>("/points/currencies"),
        api.get<{ cards: Card[] }>("/cards"),
      ]);
      setPartners(p.partners);
      setRoutes(r.routes);
      setRules(cr.rules);
      setCurrencies(cu.currencies.filter((x) => !x.isCashback));
      setCards(c.cards.filter((x) => x.isActive));
    } catch (err) {
      onError(err instanceof ApiError ? err.message : "Could not load transfer setup");
    }
  }, [onError]);

  useEffect(() => { void load(); }, [load]);

  async function removePartner(p: TransferPartner) {
    if (!confirm(`Delete ${p.name}?`)) return;
    try {
      await api.del(`/transfers/partners/${p.id}`);
      await load();
    } catch (err) {
      onError(err instanceof ApiError ? err.message : "Could not delete the partner");
    }
  }

  async function removeRoute(r: TransferRoute) {
    if (!confirm(`Remove the route from ${r.currency?.name ?? r.fromPartner?.name} to ${r.partner.name}?`)) return;
    try {
      await api.del(`/transfers/routes/${r.id}`);
      await load();
    } catch (err) {
      onError(err instanceof ApiError ? err.message : "Could not delete the route");
    }
  }

  if (!partners) return <Spinner />;

  return (
    <div className="space-y-6">
      {/* ------------------------------------------------------ programmes */}
      <Panel
        title="Transfer partners"
        subtitle="Airline and hotel programmes, what you hold in each, and what you reckon a mile is worth"
        actions={
          <button className="btn-secondary px-3 py-1.5 text-xs" onClick={() => setPartnerModal({ open: true, partner: null })}>
            Add partner
          </button>
        }
      >
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-ink-200">
                <th className="th">Programme</th>
                <th className="th text-right">Balance</th>
                <th className="th text-right">₹ / mile</th>
                <th className="th text-right">Worth</th>
                <th className="th text-right">Routes</th>
                <th className="th"><span className="sr-only">Actions</span></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-100">
              {partners.map((p) => (
                <tr key={p.id} className={p.isActive ? undefined : "opacity-55"}>
                  <td className="td">
                    <span className="font-medium text-ink-900">{p.name}</span>
                    {!p.isActive && <Badge>Off</Badge>}
                    <span className="block text-xs text-ink-500">
                      {p.kind === "HOTEL" ? "Hotel" : p.kind === "AIRLINE" ? "Airline" : "Other"}
                      {p.alliance ? ` · ${p.alliance}` : ""}
                    </span>
                  </td>
                  <td className="td tnum text-right">
                    {formatPoints(p.balance?.balanceUnits ?? 0)}
                    {p.balance && (p.balance.transferredIn > 0 || p.balance.redeemed > 0) && (
                      <span className="block text-xs text-ink-500">
                        {formatPoints(p.openingUnits)} opening
                        {p.balance.transferredIn > 0 && ` +${formatPoints(p.balance.transferredIn)} in`}
                        {p.balance.transferredOut > 0 && ` −${formatPoints(p.balance.transferredOut)} out`}
                        {p.balance.redeemed > 0 && ` −${formatPoints(p.balance.redeemed)} redeemed`}
                      </span>
                    )}
                  </td>
                  <td className="td tnum text-right text-ink-600">₹{p.valuePerUnit}</td>
                  <td className="td tnum text-right">{formatINR(p.balance?.value ?? 0)}</td>
                  <td className="td tnum text-right text-ink-600">{p._count?.routes ?? 0}</td>
                  <td className="td whitespace-nowrap text-right">
                    <button className="text-xs text-accent-700 underline" onClick={() => setPartnerModal({ open: true, partner: p })}>
                      Edit
                    </button>
                    <button className="ml-3 text-xs text-red-700 underline" onClick={() => removePartner(p)}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-3 text-xs text-ink-500">
          Balances are worked out: opening figure, plus transfers in, minus anything sent onward or redeemed. If a
          statement disagrees, correct the opening figure and the rest follows.
        </p>
      </Panel>

      {/* ---------------------------------------------------------- routes */}
      <Panel
        title="Routes"
        subtitle="How each reward programme converts into a partner — the ratio and any transfer blocks"
        actions={
          <button
            className="btn-secondary px-3 py-1.5 text-xs"
            onClick={() => setRouteModal({ open: true, route: null })}
            disabled={currencies.length === 0 || partners.length === 0}
          >
            Add route
          </button>
        }
      >
        {routes.length === 0 ? (
          <EmptyState
            title="No routes yet"
            hint="A route says that, for instance, HDFC Reward Points become KrisFlyer miles at 1:1."
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-ink-200">
                  <th className="th">From programme</th>
                  <th className="th">To partner</th>
                  <th className="th text-right">Ratio</th>
                  <th className="th text-right">Minimum</th>
                  <th className="th text-right">Blocks of</th>
                  <th className="th"><span className="sr-only">Actions</span></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-100">
                {routes.map((r) => (
                  <tr key={r.id} className={r.isActive ? undefined : "opacity-55"}>
                    <td className="td text-ink-800">
                      {r.currency?.name ?? r.fromPartner?.name ?? "—"}
                      {r.fromPartner && <span className="block text-xs text-ink-500">miles already held</span>}
                    </td>
                    <td className="td">
                      <span className="text-ink-800">{r.partner.name}</span>
                      {!r.isActive && <Badge>Off</Badge>}
                    </td>
                    <td className="td tnum text-right font-medium">
                      {r.fromPoints}:{r.toUnits}
                      {r.bonusType === "PER_BLOCK" && r.bonusPerBlock && r.bonusBlockSize && (
                        <span className="block text-xs text-emerald-700">
                          +{formatPoints(r.bonusPerBlock)} / {formatPoints(r.bonusBlockSize)}
                        </span>
                      )}
                      {r.bonusType === "PERCENT" && r.bonusPercent && (
                        <span className="block text-xs text-emerald-700">+{r.bonusPercent}%</span>
                      )}
                    </td>
                    <td className="td tnum text-right text-ink-600">{r.minTransfer ? formatPoints(r.minTransfer) : "—"}</td>
                    <td className="td tnum text-right text-ink-600">{r.increment ? formatPoints(r.increment) : "—"}</td>
                    <td className="td whitespace-nowrap text-right">
                      <button className="text-xs text-accent-700 underline" onClick={() => setRouteModal({ open: true, route: r })}>
                        Edit
                      </button>
                      <button className="ml-3 text-xs text-red-700 underline" onClick={() => removeRoute(r)}>Remove</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      {/* ------------------------------------------------------ card rules */}
      <TransferCapSchemes cards={cards} partners={partners ?? []} onError={onError} onNotice={onNotice} />

      <Panel
        title="Card transfer caps"
        subtitle="What each card is allowed to send, and to whom — set per card"
      >
        {cards.length === 0 ? (
          <EmptyState title="No cards yet" />
        ) : (
          <ul className="divide-y divide-ink-100">
            {cards.map((card) => {
              const cardRules = rules.filter((r) => r.cardId === card.id);
              const allowed = cardRules.filter((r) => r.isAllowed);
              const blocked = cardRules.filter((r) => !r.isAllowed);
              return (
                <li key={card.id} className="flex flex-wrap items-start gap-4 py-4 first:pt-0 last:pb-0">
                  <span className="h-10 w-1.5 shrink-0 rounded-full" style={{ background: card.color }} aria-hidden />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-ink-900">{card.name}</p>
                    <p className="mt-0.5 text-xs text-ink-600">
                      {card.transferCapPoints
                        ? `Up to ${formatPoints(card.transferCapPoints)} points ${periodLabel(card.transferCapPeriod)}`
                        : "No overall transfer cap"}
                      {card.transferPartnersRestricted
                        ? ` · only its ${allowed.length} listed partner${allowed.length === 1 ? "" : "s"}`
                        : blocked.length > 0
                          ? ` · ${blocked.length} partner${blocked.length === 1 ? "" : "s"} blocked`
                          : " · all partners its programme reaches"}
                    </p>
                    {cardRules.filter((r) => r.capPoints).length > 0 && (
                      <ul className="mt-1.5 space-y-0.5">
                        {cardRules
                          .filter((r) => r.capPoints)
                          .map((r) => (
                            <li key={r.id} className="tnum text-xs text-ink-500">
                              {r.partner.name}: max {formatPoints(r.capPoints!)} pts {periodLabel(r.capPeriod)}
                              {r.fromPoints != null && r.toUnits != null && ` · ${r.fromPoints}:${r.toUnits}`}
                            </li>
                          ))}
                      </ul>
                    )}
                  </div>
                  <button className="btn-secondary px-3 py-1.5 text-xs" onClick={() => setCardModal(card)}>
                    Edit caps
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </Panel>

      <PartnerModal
        state={partnerModal}
        onClose={() => setPartnerModal({ open: false, partner: null })}
        onSaved={() => { setPartnerModal({ open: false, partner: null }); void load(); }}
      />
      <RouteModal
        state={routeModal}
        currencies={currencies}
        partners={partners}
        onClose={() => setRouteModal({ open: false, route: null })}
        onSaved={() => { setRouteModal({ open: false, route: null }); void load(); }}
      />
      <CardCapsModal
        card={cardModal}
        partners={partners}
        routes={routes}
        rules={rules.filter((r) => r.cardId === cardModal?.id)}
        onClose={() => setCardModal(null)}
        onSaved={() => { setCardModal(null); onNotice("Transfer caps saved."); void load(); }}
        onError={onError}
      />
    </div>
  );
}

/* -------------------------------------------------------------- partner */

function PartnerModal({
  state,
  onClose,
  onSaved,
}: {
  state: { open: boolean; partner: TransferPartner | null };
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState({ name: "", kind: "AIRLINE", alliance: "", valuePerUnit: "0.5", openingUnits: "0", isActive: true, notes: "", personId: "" });
  const [people, setPeople] = useState<Person[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const p = state.partner;
    setForm({
      name: p?.name ?? "",
      kind: p?.kind ?? "AIRLINE",
      alliance: p?.alliance ?? "",
      valuePerUnit: String(p?.valuePerUnit ?? 0.5),
      openingUnits: String(p?.openingUnits ?? 0),
      personId: p?.personId ?? "",
      isActive: p?.isActive ?? true,
      notes: p?.notes ?? "",
    });
    setError(null);
  }, [state.partner, state.open]);

  useEffect(() => {
    if (!state.open) return;
    void api.get<{ people: Person[] }>("/people").then((r) => setPeople(r.people)).catch(() => setPeople([]));
  }, [state.open]);

  async function save(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const payload = {
      name: form.name.trim(),
      kind: form.kind,
      alliance: form.alliance.trim() || null,
      valuePerUnit: Number(form.valuePerUnit) || 0,
      openingUnits: Number(form.openingUnits) || 0,
      personId: form.personId || null,
      isActive: form.isActive,
      notes: form.notes.trim() || null,
    };
    try {
      if (state.partner) await api.put(`/transfers/partners/${state.partner.id}`, payload);
      else await api.post("/transfers/partners", payload);
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not save the partner");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal open={state.open} onClose={onClose} title={state.partner ? `Edit ${state.partner.name}` : "Add a transfer partner"}>
      <form onSubmit={save} className="space-y-4">
        <div>
          <label className="label" htmlFor="p-name">Programme name</label>
          <input id="p-name" className="input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Singapore Airlines KrisFlyer" />
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="label" htmlFor="p-kind">Type</label>
            <select id="p-kind" className="input" value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })}>
              <option value="AIRLINE">Airline</option>
              <option value="HOTEL">Hotel</option>
              <option value="OTHER">Other</option>
            </select>
          </div>
          <div>
            <label className="label" htmlFor="p-alliance">Alliance (optional)</label>
            <input id="p-alliance" className="input" value={form.alliance} onChange={(e) => setForm({ ...form, alliance: e.target.value })} placeholder="Star Alliance" />
          </div>
          <div>
            <label className="label" htmlFor="p-value">Value per mile (₹)</label>
            <input id="p-value" className="input tnum" type="number" min={0} step="0.01" value={form.valuePerUnit} onChange={(e) => setForm({ ...form, valuePerUnit: e.target.value })} />
          </div>
          <div>
            <label className="label" htmlFor="p-balance">Opening balance</label>
            <input id="p-balance" className="input tnum" type="number" min={0} value={form.openingUnits} onChange={(e) => setForm({ ...form, openingUnits: e.target.value })} />
            <p className="mt-1 text-xs text-ink-500">
              Miles held before anything tracked here. Transfers and redemptions adjust the live balance from this.
            </p>
          </div>
        </div>
        <div>
          {people.length > 0 && (
            <div className="mb-4">
              <label className="label" htmlFor="p-person">Whose account is this?</label>
              <select
                id="p-person"
                className="input"
                value={form.personId}
                onChange={(e) => setForm({ ...form, personId: e.target.value })}
              >
                <option value="">Shared — anyone&apos;s points can go in</option>
                {people.map((x) => (
                  <option key={x.id} value={x.id}>{x.name}</option>
                ))}
              </select>
              <p className="mt-1 text-xs text-ink-500">
                Naming the holder lets a redemption plan skip cards whose points cannot reach this account.
              </p>
            </div>
          )}

          <label className="label" htmlFor="p-notes">Notes</label>
          <input id="p-notes" className="input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
        </div>
        <label className="flex items-center gap-2 text-sm text-ink-700">
          <input type="checkbox" className="h-4 w-4 rounded border-ink-300 text-accent-600 focus:ring-accent-500" checked={form.isActive} onChange={(e) => setForm({ ...form, isActive: e.target.checked })} />
          Active — include it in planning
        </label>
        {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn-primary" disabled={busy}>{busy ? "Saving…" : "Save"}</button>
        </div>
      </form>
    </Modal>
  );
}

/* ---------------------------------------------------------------- route */

function RouteModal({
  state,
  currencies,
  partners,
  onClose,
  onSaved,
}: {
  state: { open: boolean; route: TransferRoute | null };
  currencies: RewardCurrency[];
  partners: TransferPartner[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState({
    sourceKind: "CURRENCY" as "CURRENCY" | "PARTNER",
    currencyId: "",
    fromPartnerId: "",
    partnerId: "",
    fromPoints: "1",
    toUnits: "1",
    minTransfer: "1000",
    increment: "1000",
    bonusType: "NONE",
    bonusPerBlock: "",
    bonusBlockSize: "",
    bonusPercent: "",
    bonusMinPoints: "",
    bonusEndsOn: "",
    isActive: true,
    notes: "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const r = state.route;
    setForm({
      sourceKind: r?.fromPartnerId ? "PARTNER" : "CURRENCY",
      currencyId: r?.currencyId ?? currencies[0]?.id ?? "",
      fromPartnerId: r?.fromPartnerId ?? partners[0]?.id ?? "",
      partnerId: r?.partnerId ?? partners[0]?.id ?? "",
      fromPoints: String(r?.fromPoints ?? 1),
      toUnits: String(r?.toUnits ?? 1),
      minTransfer: r?.minTransfer != null ? String(r.minTransfer) : "",
      increment: r?.increment != null ? String(r.increment) : "",
      bonusType: r?.bonusType ?? "NONE",
      bonusPerBlock: r?.bonusPerBlock != null ? String(r.bonusPerBlock) : "",
      bonusBlockSize: r?.bonusBlockSize != null ? String(r.bonusBlockSize) : "",
      bonusPercent: r?.bonusPercent != null ? String(r.bonusPercent) : "",
      bonusMinPoints: r?.bonusMinPoints != null ? String(r.bonusMinPoints) : "",
      bonusEndsOn: r?.bonusEndsOn ? r.bonusEndsOn.slice(0, 10) : "",
      isActive: r?.isActive ?? true,
      notes: r?.notes ?? "",
    });
    setError(null);
  }, [state.route, state.open, currencies, partners]);

  async function save(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const payload = {
      currencyId: form.sourceKind === "CURRENCY" ? form.currencyId : null,
      fromPartnerId: form.sourceKind === "PARTNER" ? form.fromPartnerId : null,
      partnerId: form.partnerId,
      fromPoints: Number(form.fromPoints) || 1,
      toUnits: Number(form.toUnits) || 1,
      minTransfer: form.minTransfer.trim() === "" ? null : Number(form.minTransfer),
      increment: form.increment.trim() === "" ? null : Number(form.increment),
      bonusType: form.bonusType,
      bonusPerBlock: form.bonusPerBlock.trim() === "" ? null : Number(form.bonusPerBlock),
      bonusBlockSize: form.bonusBlockSize.trim() === "" ? null : Number(form.bonusBlockSize),
      bonusPercent: form.bonusPercent.trim() === "" ? null : Number(form.bonusPercent),
      bonusMinPoints: form.bonusMinPoints.trim() === "" ? null : Number(form.bonusMinPoints),
      bonusEndsOn: form.bonusEndsOn || null,
      isActive: form.isActive,
      notes: form.notes.trim() || null,
    };
    try {
      if (state.route) await api.put(`/transfers/routes/${state.route.id}`, payload);
      else await api.post("/transfers/routes", payload);
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not save the route");
    } finally {
      setBusy(false);
    }
  }

  const ratio = Number(form.fromPoints) > 0 ? Number(form.toUnits) / Number(form.fromPoints) : 0;
  // Show the worked example at a size that actually triggers a block bonus,
  // otherwise the preview would suggest the bonus does nothing.
  const blockSize = Number(form.bonusBlockSize) || 0;
  const sample = form.bonusType === "PER_BLOCK" && blockSize > 0 ? blockSize : 10000;
  const sampleBonus =
    form.bonusType === "PER_BLOCK" && blockSize > 0
      ? Math.floor(sample / blockSize) * (Number(form.bonusPerBlock) || 0)
      : form.bonusType === "PERCENT" && Number(form.bonusPercent) > 0
        ? Math.floor((Math.floor(sample * ratio) * Number(form.bonusPercent)) / 100)
        : 0;

  return (
    <Modal open={state.open} onClose={onClose} title={state.route ? "Edit route" : "Add a transfer route"} wide>
      <form onSubmit={save} className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="label" htmlFor="r-kind">Points start at</label>
            <select
              id="r-kind"
              className="input"
              disabled={state.route != null}
              value={form.sourceKind}
              onChange={(e) => setForm({ ...form, sourceKind: e.target.value as "CURRENCY" | "PARTNER" })}
            >
              <option value="CURRENCY">A card's reward programme</option>
              <option value="PARTNER">Another partner (onward transfer)</option>
            </select>
          </div>
          {form.sourceKind === "CURRENCY" ? (
            <div>
              <label className="label" htmlFor="r-currency">From reward programme</label>
              <select id="r-currency" className="input" required disabled={state.route != null} value={form.currencyId} onChange={(e) => setForm({ ...form, currencyId: e.target.value })}>
                {currencies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
          ) : (
            <div>
              <label className="label" htmlFor="r-frompartner">From partner</label>
              <select id="r-frompartner" className="input" required disabled={state.route != null} value={form.fromPartnerId} onChange={(e) => setForm({ ...form, fromPartnerId: e.target.value })}>
                {partners.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
              <p className="mt-1 text-xs text-ink-500">
                Chains behind any route into this partner — Amex to Bonvoy to an airline.
              </p>
            </div>
          )}
          <div>
            <label className="label" htmlFor="r-partner">To partner</label>
            <select id="r-partner" className="input" required disabled={state.route != null} value={form.partnerId} onChange={(e) => setForm({ ...form, partnerId: e.target.value })}>
              {partners.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <div>
            <label className="label" htmlFor="r-from">Points</label>
            <input id="r-from" className="input tnum" type="number" min={0.01} step="0.01" required value={form.fromPoints} onChange={(e) => setForm({ ...form, fromPoints: e.target.value })} />
          </div>
          <div>
            <label className="label" htmlFor="r-to">become miles</label>
            <input id="r-to" className="input tnum" type="number" min={0.01} step="0.01" required value={form.toUnits} onChange={(e) => setForm({ ...form, toUnits: e.target.value })} />
          </div>
          <div>
            <label className="label" htmlFor="r-min">Minimum transfer</label>
            <input id="r-min" className="input tnum" type="number" min={0} value={form.minTransfer} onChange={(e) => setForm({ ...form, minTransfer: e.target.value })} placeholder="No minimum" />
          </div>
          <div>
            <label className="label" htmlFor="r-inc">Transfer in blocks of</label>
            <input id="r-inc" className="input tnum" type="number" min={0} value={form.increment} onChange={(e) => setForm({ ...form, increment: e.target.value })} placeholder="Any amount" />
          </div>
        </div>

        <section>
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-ink-500">Transfer bonus</h3>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <label className="label" htmlFor="r-bonustype">Bonus</label>
              <select id="r-bonustype" className="input" value={form.bonusType} onChange={(e) => setForm({ ...form, bonusType: e.target.value })}>
                <option value="NONE">None</option>
                <option value="PER_BLOCK">Fixed miles per block of points</option>
                <option value="PERCENT">Percentage uplift (a promo)</option>
              </select>
            </div>
            {form.bonusType === "PER_BLOCK" && (
              <>
                <div>
                  <label className="label" htmlFor="r-bperblock">Bonus miles</label>
                  <input id="r-bperblock" className="input tnum" type="number" min={0} value={form.bonusPerBlock} onChange={(e) => setForm({ ...form, bonusPerBlock: e.target.value })} placeholder="5000" />
                </div>
                <div>
                  <label className="label" htmlFor="r-bblock">per every</label>
                  <input id="r-bblock" className="input tnum" type="number" min={1} value={form.bonusBlockSize} onChange={(e) => setForm({ ...form, bonusBlockSize: e.target.value })} placeholder="60000 points" />
                </div>
                <p className="text-xs text-ink-500 sm:col-span-2">
                  Only whole blocks pay — Marriott's 5,000 per 60,000 means 90,000 points earns one bonus, not one
                  and a half.
                </p>
              </>
            )}
            {form.bonusType === "PERCENT" && (
              <>
                <div>
                  <label className="label" htmlFor="r-bpct">Extra miles (%)</label>
                  <input id="r-bpct" className="input tnum" type="number" min={0} step="1" value={form.bonusPercent} onChange={(e) => setForm({ ...form, bonusPercent: e.target.value })} placeholder="30" />
                </div>
                <div>
                  <label className="label" htmlFor="r-bmin">Only above (points)</label>
                  <input id="r-bmin" className="input tnum" type="number" min={0} value={form.bonusMinPoints} onChange={(e) => setForm({ ...form, bonusMinPoints: e.target.value })} placeholder="No floor" />
                </div>
              </>
            )}
            {form.bonusType !== "NONE" && (
              <div className="sm:col-span-2">
                <label className="label" htmlFor="r-bends">Bonus ends on (optional)</label>
                <input id="r-bends" className="input" type="date" value={form.bonusEndsOn} onChange={(e) => setForm({ ...form, bonusEndsOn: e.target.value })} />
                <p className="mt-1 text-xs text-ink-500">Past this date the bonus stops being counted.</p>
              </div>
            )}
          </div>
        </section>

        <p className="rounded-lg border border-ink-200 bg-ink-50 px-3 py-2 text-sm text-ink-700">
          {formatPoints(sample)} points become{" "}
          <strong className="tnum">{formatPoints(Math.floor(sample * ratio) + sampleBonus)}</strong> miles
          {sampleBonus > 0 && ` (including ${formatPoints(sampleBonus)} bonus)`}.
        </p>

        <label className="flex items-center gap-2 text-sm text-ink-700">
          <input type="checkbox" className="h-4 w-4 rounded border-ink-300 text-accent-600 focus:ring-accent-500" checked={form.isActive} onChange={(e) => setForm({ ...form, isActive: e.target.checked })} />
          Route is open
        </label>

        {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn-primary" disabled={busy}>{busy ? "Saving…" : "Save route"}</button>
        </div>
      </form>
    </Modal>
  );
}

/* ----------------------------------------------------------- card caps */

function CardCapsModal({
  card,
  partners,
  routes,
  rules,
  onClose,
  onSaved,
  onError,
}: {
  card: Card | null;
  partners: TransferPartner[];
  routes: TransferRoute[];
  rules: CardTransferRule[];
  onClose: () => void;
  onSaved: () => void;
  onError: (m: string) => void;
}) {
  const [capPoints, setCapPoints] = useState("");
  const [capPeriod, setCapPeriod] = useState("YEAR");
  const [restricted, setRestricted] = useState(false);
  const [rows, setRows] = useState<Record<string, { isAllowed: boolean; capPoints: string; capPeriod: string; fromPoints: string; toUnits: string }>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!card) return;
    setCapPoints(card.transferCapPoints != null ? String(card.transferCapPoints) : "");
    setCapPeriod(card.transferCapPeriod ?? "YEAR");
    setRestricted(card.transferPartnersRestricted ?? false);
    const next: typeof rows = {};
    for (const p of partners) {
      const rule = rules.find((r) => r.partnerId === p.id);
      next[p.id] = {
        isAllowed: rule ? rule.isAllowed : !card.transferPartnersRestricted,
        capPoints: rule?.capPoints != null ? String(rule.capPoints) : "",
        capPeriod: rule?.capPeriod ?? "YEAR",
        fromPoints: rule?.fromPoints != null ? String(rule.fromPoints) : "",
        toUnits: rule?.toUnits != null ? String(rule.toUnits) : "",
      };
    }
    setRows(next);
    setError(null);
    // rules is derived from card, so card alone is the right trigger here
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [card, partners]);

  if (!card) return null;

  // Allowing a partner on the card does nothing unless the card's reward
  // programme has a route to it, so say which ones are actually reachable.
  const reachable = new Set(
    routes.filter((r) => r.currencyId === card.currencyId && r.isActive).map((r) => r.partnerId),
  );
  const programmeName = card.currency?.name ?? "this card's programme";

  async function save() {
    setBusy(true);
    setError(null);
    try {
      await api.put(`/cards/${card!.id}`, {
        transferCapPoints: capPoints.trim() === "" ? null : Number(capPoints),
        transferCapPeriod: capPeriod,
        transferPartnersRestricted: restricted,
      });

      // Only send rows that actually say something — an allowed partner with
      // no cap and no override is the default and needs no rule.
      for (const partner of partners) {
        const row = rows[partner.id];
        if (!row) continue;
        const defaultAllowed = !restricted;
        const meaningful =
          row.isAllowed !== defaultAllowed || row.capPoints.trim() !== "" || row.fromPoints.trim() !== "";
        const existing = rules.find((r) => r.partnerId === partner.id);

        if (!meaningful) {
          if (existing) await api.del(`/transfers/card-rules/${existing.id}`);
          continue;
        }
        await api.post("/transfers/card-rules", {
          cardId: card!.id,
          partnerId: partner.id,
          isAllowed: row.isAllowed,
          capPoints: row.capPoints.trim() === "" ? null : Number(row.capPoints),
          capPeriod: row.capPeriod,
          fromPoints: row.fromPoints.trim() === "" ? null : Number(row.fromPoints),
          toUnits: row.toUnits.trim() === "" ? null : Number(row.toUnits),
        });
      }
      onSaved();
    } catch (err) {
      const message = err instanceof ApiError ? err.message : "Could not save the caps";
      setError(message);
      onError(message);
    } finally {
      setBusy(false);
    }
  }

  const set = (partnerId: string, patch: Partial<(typeof rows)[string]>) =>
    setRows((r) => ({ ...r, [partnerId]: { ...r[partnerId], ...patch } }));

  return (
    <Modal
      open
      onClose={onClose}
      title={`Transfer caps — ${card.name}`}
      wide
      footer={
        <div className="flex justify-end gap-2">
          <button className="btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn-primary" onClick={save} disabled={busy}>{busy ? "Saving…" : "Save caps"}</button>
        </div>
      }
    >
      <div className="space-y-5">
        <section>
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-ink-500">Overall cap</h3>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="label" htmlFor="cc-points">Total points this card may transfer</label>
              <input id="cc-points" className="input tnum" type="number" min={0} value={capPoints} onChange={(e) => setCapPoints(e.target.value)} placeholder="No cap" />
            </div>
            <div>
              <label className="label" htmlFor="cc-period">Resetting</label>
              <select id="cc-period" className="input" value={capPeriod} onChange={(e) => setCapPeriod(e.target.value)}>
                {CAP_PERIODS.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
              </select>
            </div>
          </div>
          <label className="mt-3 flex items-center gap-2 text-sm text-ink-700">
            <input type="checkbox" className="h-4 w-4 rounded border-ink-300 text-accent-600 focus:ring-accent-500" checked={restricted} onChange={(e) => setRestricted(e.target.checked)} />
            This card only transfers to the partners I tick below
          </label>
          <p className="mt-1 text-xs text-ink-500">
            Leave it off and the card reaches every partner its reward programme has a route to, except any you untick.
          </p>
        </section>

        <section>
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-ink-500">Per-partner</h3>
          <div className="max-h-80 overflow-y-auto rounded-lg border border-ink-200">
            <table className="w-full">
              <thead className="sticky top-0 bg-white">
                <tr className="border-b border-ink-200">
                  <th className="th">Partner</th>
                  <th className="th text-center">Allowed</th>
                  <th className="th text-right">Cap (points)</th>
                  <th className="th">Resetting</th>
                  <th className="th text-right">Ratio override</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-100">
                {partners.map((p) => {
                  const row = rows[p.id];
                  if (!row) return null;
                  const hasRoute = reachable.has(p.id);
                  return (
                    <tr key={p.id} className={hasRoute ? undefined : "bg-ink-50/50"}>
                      <td className="td text-sm">
                        {p.name}
                        {!hasRoute && (
                          <span className="block text-xs text-amber-700">
                            No route from {programmeName} — add one above for this to do anything
                          </span>
                        )}
                      </td>
                      <td className="td text-center">
                        <input
                          type="checkbox"
                          className="h-4 w-4 rounded border-ink-300 text-accent-600 focus:ring-accent-500"
                          checked={row.isAllowed}
                          onChange={(e) => set(p.id, { isAllowed: e.target.checked })}
                          aria-label={`Allow transfers to ${p.name}`}
                        />
                      </td>
                      <td className="td text-right">
                        <input
                          className="input tnum w-28 px-2 py-1 text-right text-sm"
                          type="number"
                          min={0}
                          value={row.capPoints}
                          onChange={(e) => set(p.id, { capPoints: e.target.value })}
                          placeholder="None"
                          aria-label={`Cap for ${p.name}`}
                        />
                      </td>
                      <td className="td">
                        <select
                          className="input w-32 px-2 py-1 text-sm"
                          value={row.capPeriod}
                          onChange={(e) => set(p.id, { capPeriod: e.target.value })}
                          aria-label={`Cap period for ${p.name}`}
                        >
                          {CAP_PERIODS.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                        </select>
                      </td>
                      <td className="td text-right">
                        <div className="flex items-center justify-end gap-1">
                          <input className="input tnum w-14 px-1.5 py-1 text-right text-sm" type="number" min={0} step="0.01" value={row.fromPoints} onChange={(e) => set(p.id, { fromPoints: e.target.value })} placeholder="—" aria-label={`Ratio points for ${p.name}`} />
                          <span className="text-xs text-ink-400">:</span>
                          <input className="input tnum w-14 px-1.5 py-1 text-right text-sm" type="number" min={0} step="0.01" value={row.toUnits} onChange={(e) => set(p.id, { toUnits: e.target.value })} placeholder="—" aria-label={`Ratio miles for ${p.name}`} />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="mt-2 text-xs text-ink-500">
            A ratio override needs both sides. Leave both blank to use the programme's own ratio.
          </p>
        </section>

        {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}
      </div>
    </Modal>
  );
}
